"use client";

import { ReactNode, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ArrowLeft, LayoutDashboardIcon, X } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { FooterThemeToggle } from "@/components/footer-theme-toggle";
import { PandaLogo } from "@/components/panda-logo";
import { HomeUserAccountMenu } from "@/components/home-user-account-menu";
import { CreditCounter } from "@/components/credit-counter";
import { StreakBadge } from "@/components/streak/streak-badge";
import { MobileNavLinks } from "@/components/mobile-nav-links";
import { NAV_LINKS, type NavKey } from "@/lib/nav-links";
import { cn } from "@/lib/utils";

interface AppHeaderProps {
  user?: {
    email?: string;
    userMetadata?: Record<string, unknown> | null;
  } | null;
  onLogout?: () => void | Promise<void>;
  /**
   * Opens the page's login modal from the mobile menu's "Log in" row. Pages
   * without a modal can omit it — the row then links to /login.
   */
  onLogin?: () => void;
  showCredits?: boolean;
  backHref?: string;
  title?: string;
  activePage?: NavKey;
  children?: ReactNode;
  className?: string;
}

export function AppHeader({
  user,
  onLogout,
  onLogin,
  showCredits = false,
  backHref,
  title,
  activePage,
  children,
  className,
}: AppHeaderProps) {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const pathname = usePathname();

  // Lock page scroll + close on Escape while the mobile full-screen menu is
  // open, matching the homepage / pricing overlays so close behavior is
  // identical across pages.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileMenuOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileMenuOpen]);

  return (
    <>
    <header
      className={cn(
        "sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl",
        className,
      )}
    >
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
        {/* Left: back button (optional) + logo + title */}
        <div className="flex items-center gap-2.5">
          {backHref && (
            <button
              onClick={() => router.push(backHref)}
              className="cursor-pointer rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <a
            href="/"
            className="flex items-center gap-2.5 text-foreground transition-opacity hover:opacity-80"
          >
            <PandaLogo size="md" className="rounded-lg" />
            <span className="text-sm font-bold tracking-tight text-foreground">panda</span>
          </a>
          {title && (
            <>
              <span className="text-muted-foreground/40">/</span>
              <span className="text-sm font-semibold text-foreground">
                {title}
              </span>
            </>
          )}
        </div>

        {/* Right: nav links + extras + theme + account */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Desktop nav links — hidden on mobile */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.filter((link) => !link.requiresAuth || !!user).map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={cn(
                  "rounded-full px-2.5 py-1.5 text-[13px] font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                  activePage === link.key
                    ? "bg-orange-500/10 text-orange-600 dark:text-orange-400"
                    : "text-muted-foreground",
                )}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {user && <StreakBadge />}

          {showCredits && <CreditCounter />}

          {children}

          {/* Theme toggle hidden on mobile (lives inside the menu) */}
          <div className="hidden md:block">
            <ThemeToggle />
          </div>

          {user && pathname !== "/projects" && (
            <div className="pr-2 md:hidden">
              <Link href="/projects"><LayoutDashboardIcon /></Link>
            </div>
          )}

          {user && onLogout && (
            <HomeUserAccountMenu
              email={user.email}
              userMetadata={user.userMetadata}
              onLogout={onLogout}
            />
          )}

          {/* Hamburger — mobile only. Opens a full-screen menu with the
              same nav links the desktop header shows. */}
          <button
            type="button"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="ml-1 flex h-10 w-10 -mr-1 shrink-0 items-center justify-center text-foreground transition-opacity hover:opacity-70 md:hidden"
          >
            {mobileMenuOpen ? (
              <X size={20} strokeWidth={2} />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="9"
                viewBox="0 0 18 9"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M16.75 7C17.1642 7 17.5 7.33579 17.5 7.75C17.5 8.16421 17.1642 8.5 16.75 8.5H0.75C0.335786 8.5 -3.21882e-08 8.16421 0 7.75C3.21882e-08 7.33579 0.335786 7 0.75 7H16.75ZM16.75 0C17.1642 4.07381e-08 17.5 0.335787 17.5 0.75C17.5 1.16421 17.1642 1.5 16.75 1.5H0.75C0.335786 1.5 -3.21882e-08 1.16421 0 0.75C3.21882e-08 0.335786 0.335786 -4.07382e-08 0.75 0H16.75Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </header>

    {/* Mobile full-screen menu — rendered as a sibling of <header>
        (not inside it) because the header's backdrop-blur creates a
        stacking context that breaks fixed-position descendants in some
        browsers. Mirrors the home-page hamburger menu UI. */}
    {mobileMenuOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
        className="fixed inset-0 z-[70] flex flex-col bg-background md:hidden"
      >
        {/* Top bar: theme toggle pill (left) + close X (right) —
            same as the home-page hamburger menu. */}
        <div className="flex h-14 items-center justify-between px-4">
          <FooterThemeToggle />
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
            className="flex h-6 w-6 items-center justify-center text-[#1A1A1B] transition-opacity hover:opacity-70 dark:text-foreground"
          >
            <X size={24} strokeWidth={2} />
          </button>
        </div>

        <MobileNavLinks
          signedIn={!!user}
          activeKey={activePage}
          onNavigate={() => setMobileMenuOpen(false)}
          onLogin={onLogin}
        />
      </div>
    )}
    </>
  );
}
